import React, { memo, useCallback, useState } from 'react';
import { motion } from 'framer-motion';

const SIZE_CLASSES = {
  hero: 'sr-card--hero',
  large: 'sr-card--large',
  medium: 'sr-card--medium',
  standard: 'sr-card--standard',
};

/**
 * SRCard — Screening Room reusable card wrapper.
 *
 * @param {Object}  props
 * @param {'hero'|'large'|'medium'|'standard'} props.size - Card size variant
 * @param {string}  props.title      - Card heading
 * @param {string}  props.subtitle   - Secondary text below the title
 * @param {number}  [props.score]    - Optional score shown as a pill badge
 * @param {Function} [props.onClick] - Click handler; shows hover CTA when set
 * @param {string}  [props.className] - Extra classes
 * @param {React.ReactNode} props.children
 */
const SRCard = memo(function SRCard({
  size = 'standard',
  title,
  subtitle,
  score,
  onClick,
  className = '',
  children,
}) {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.standard;

  const getScoreTier = (s) => {
    if (s >= 85) return 'greenlit';
    if (s >= 70) return 'high';
    if (s >= 50) return 'mid';
    return 'low';
  };

  return (
    <motion.div
      className={`sr-card ${sizeClass} ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ scale: 1.008 }}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Header */}
      {(title || score !== undefined) && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 16,
          gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {title && (
              <h3
                style={{
                  fontFamily: 'var(--sr-font-display)',
                  fontSize: size === 'hero' ? 'var(--sr-text-display)' : 'var(--sr-text-heading)',
                  fontWeight: 600,
                  color: 'var(--sr-text-primary)',
                  lineHeight: 1.2,
                  margin: 0,
                }}
              >
                {title}
              </h3>
            )}
            {subtitle && (
              <p
                style={{
                  fontFamily: 'var(--sr-font-body)',
                  fontSize: 'var(--sr-text-caption)',
                  fontWeight: 500,
                  color: 'var(--sr-text-tertiary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginTop: 6,
                  marginBottom: 0,
                }}
              >
                {subtitle}
              </p>
            )}
          </div>

          {score !== undefined && (
            <span className={`sr-score-pill sr-score-pill--${getScoreTier(score)}`}>
              {score}
            </span>
          )}
        </div>
      )}

      {/* Body */}
      <div style={{ position: 'relative', zIndex: 3, paddingBottom: onClick ? 28 : 0 }}>
        {children}
      </div>

      {/* Hover CTA */}
      {onClick && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 4 }}
          transition={{ duration: 0.2 }}
          style={{
            position: 'absolute',
            bottom: size === 'hero' ? 40 : size === 'large' ? 32 : 20,
            right: size === 'hero' ? 40 : size === 'large' ? 32 : 20,
            fontFamily: 'var(--sr-font-body)',
            fontSize: 'var(--sr-text-caption)',
            color: 'var(--sr-gold)',
            fontWeight: 500,
            letterSpacing: '0.02em',
            zIndex: 4,
            pointerEvents: 'none',
          }}
        >
          Click to explore &rarr;
        </motion.div>
      )}
    </motion.div>
  );
});

export default SRCard;
